import { sequence, msg, tick } from '@boba-cli/tea';

// src/messages.ts
var TickMsg = class {
  constructor(id, tag) {
    this.id = id;
    this.tag = tag;
  }
  _tag = "stopwatch-tick";
};
var StartStopMsg = class {
  constructor(id, running) {
    this.id = id;
    this.running = running;
  }
  _tag = "stopwatch-start-stop";
};
var ResetMsg = class {
  constructor(id) {
    this.id = id;
  }
  _tag = "stopwatch-reset";
};
var lastId = 0;
function nextId() {
  return ++lastId;
}
var StopwatchModel = class _StopwatchModel {
  interval;
  #elapsed;
  #id;
  #tag;
  #running;
  constructor(options) {
    this.interval = options.interval;
    this.#elapsed = options.elapsed;
    this.#running = options.running;
    this.#id = options.id;
    this.#tag = options.tag;
  }
  /** Create a new stopwatch. */
  static new(options = {}) {
    const interval = options.interval ?? 1e3;
    return new _StopwatchModel({
      elapsed: 0,
      interval,
      running: false,
      id: nextId(),
      tag: 0
    });
  }
  /** Create a new stopwatch with explicit interval. */
  static withInterval(interval) {
    return _StopwatchModel.new({ interval });
  }
  /** Unique ID for message routing. */
  id() {
    return this.#id;
  }
  /** Whether the stopwatch is running. */
  running() {
    return this.#running;
  }
  /** Milliseconds elapsed. */
  elapsed() {
    return this.#elapsed;
  }
  /** Start the stopwatch on init. */
  init() {
    return this.start();
  }
  /** Update the stopwatch in response to a message. */
  update(msg) {
    if (msg instanceof StartStopMsg) {
      if (msg.id !== this.#id) {
        return [this, null];
      }
      const next = new _StopwatchModel({
        elapsed: this.#elapsed,
        interval: this.interval,
        running: msg.running,
        id: this.#id,
        tag: this.#tag
      });
      return [next, null];
    }
    if (msg instanceof ResetMsg) {
      if (msg.id !== this.#id) {
        return [this, null];
      }
      const next = new _StopwatchModel({
        elapsed: 0,
        interval: this.interval,
        running: this.#running,
        id: this.#id,
        tag: this.#tag
      });
      return [next, null];
    }
    if (msg instanceof TickMsg) {
      if (!this.running() || msg.id !== this.#id) {
        return [this, null];
      }
      if (msg.tag !== this.#tag) {
        return [this, null];
      }
      const next = new _StopwatchModel({
        elapsed: this.#elapsed + this.interval,
        interval: this.interval,
        running: this.#running,
        id: this.#id,
        tag: this.#tag + 1
      });
      return [next, next.tick()];
    }
    return [this, null];
  }
  /** Render elapsed time as a human-readable string. */
  view() {
    return formatDuration(this.#elapsed);
  }
  /** Command to start the stopwatch. */
  start() {
    return sequence(msg(new StartStopMsg(this.#id, true)), this.tick());
  }
  /** Command to stop the stopwatch. */
  stop() {
    return msg(new StartStopMsg(this.#id, false));
  }
  /** Command to toggle running state. */
  toggle() {
    return this.running() ? this.stop() : this.start();
  }
  /** Command to reset elapsed time. */
  reset() {
    return msg(new ResetMsg(this.#id));
  }
  tick() {
    const id = this.#id;
    const tag = this.#tag;
    return tick(this.interval, () => new TickMsg(id, tag));
  }
};
function formatDuration(ms) {
  const seconds = Math.floor(ms / 1e3) % 60;
  const minutes = Math.floor(ms / 6e4) % 60;
  const hours = Math.floor(ms / 36e5);
  if (hours > 0) return `${hours}h${minutes}m${seconds}s`;
  if (minutes > 0) return `${minutes}m${seconds}s`;
  return `${seconds}s`;
}

export { ResetMsg, StartStopMsg, StopwatchModel, TickMsg };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map